import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  Add as AddIcon,
  Remove,
  Delete,
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import { useAppTheme } from '@contexts/ThemeContext';
import {
  updateEmployeePayroll,
  getEmployeePayrollByIds,
  getPayrollById,
} from '@/services/HrService/PayrollService';

interface SalaryComponent {
  id: string;
  label: string;
  amount: number | '';
  percentage: number | '';
  operation: 'add' | 'subtract';
  isPercentage: boolean;
}

/**
 * Statutory deductions saved against the employee. Kept as strings so the
 * inputs stay controlled while being edited; parsed on save and for totals.
 */
interface StatutoryDeductions {
  pfContribution: string;
  incomeTaxTds: string;
  professionalTax: string;
}

const STATUTORY_FIELDS: { key: keyof StatutoryDeductions; label: string }[] = [
  { key: 'pfContribution', label: 'PF Contribution' },
  { key: 'incomeTaxTds', label: 'Income Tax (TDS)' },
  { key: 'professionalTax', label: 'Professional Tax' },
];

const EditEmployeePayroll: React.FC = () => {
  const { payrollId, employeePayrollId } = useParams<{ payrollId: string; employeePayrollId: string }>();
  const navigate = useNavigate();
  const { isDarkMode } = useAppTheme();

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [payrollInfo, setPayrollInfo] = useState<any>(null);
  const [employeeForm, setEmployeeForm] = useState({
    employeeId: '',
    employeeName: '',
    grossSalary: '',
  });
  const [salaryComponents, setSalaryComponents] = useState<SalaryComponent[]>([]);
  const [statutoryDeductions, setStatutoryDeductions] = useState<StatutoryDeductions>({
    pfContribution: '0',
    incomeTaxTds: '0',
    professionalTax: '0',
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch payroll info and employee payroll
  useEffect(() => {
    const fetchData = async () => {
      if (!payrollId || !employeePayrollId) return;

      try {
        // Fetch payroll info
        const payrollResponse = await getPayrollById(parseInt(payrollId));
        if (payrollResponse.success && payrollResponse.data) {
          setPayrollInfo(payrollResponse.data);
        }

        // Fetch employee payroll using the new endpoint with both IDs
        const empPayrollResponse = await getEmployeePayrollByIds(parseInt(payrollId), parseInt(employeePayrollId));
        if (empPayrollResponse.success && empPayrollResponse.data) {
          const ep = empPayrollResponse.data;

          setEmployeeForm({
            employeeId: ep.employee_id,
            employeeName: ep.employee_name,
            grossSalary: ep.gross_salary.toString(),
          });

          // Previously saved statutory deductions; absent means nothing saved yet
          setStatutoryDeductions({
            pfContribution: (ep.pf_contribution ?? 0).toString(),
            incomeTaxTds: (ep.income_tax_tds ?? 0).toString(),
            professionalTax: (ep.professional_tax ?? 0).toString(),
          });

          const components = (ep.salary_components || []).map((comp: any, index: number) => ({
            id: `comp-${index}-${Date.now()}`,  // Generate ID on frontend since API doesn't provide it
            label: comp.component_name,
            amount: comp.component_type === 'fixed' ? parseFloat(comp.value) : '',
            percentage: comp.component_type === 'percentage' ? parseFloat(comp.value) : '',
            operation: comp.operation,
            isPercentage: comp.component_type === 'percentage'
          }));

          setSalaryComponents(components);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load employee payroll data',
          severity: 'error',
        });
      } finally {
        setFetchLoading(false);
      }
    };

    fetchData();
  }, [payrollId, employeePayrollId]);

  const calculateComponentValue = (comp: SalaryComponent, gross: number): number => {
    return comp.isPercentage
      ? gross * ((comp.percentage as number) || 0) / 100
      : (comp.amount as number) || 0;
  };

  const calculateTotalStatutory = (): number =>
    STATUTORY_FIELDS.reduce(
      (sum, { key }) => sum + (parseFloat(statutoryDeductions[key] || '0') || 0),
      0
    );

  // Net Salary = Total Earnings - Total Deductions - Total Statutory Deductions
  const calculateNetSalary = (): number => {
    const gross = parseFloat(employeeForm.grossSalary || '0') || 0;
    let net = gross;
    salaryComponents.forEach((c) => {
      const val = calculateComponentValue(c, gross);
      net += c.operation === 'add' ? val : -val;
    });
    return net - calculateTotalStatutory();
  };

  const handleUpdateStatutory = (key: keyof StatutoryDeductions, value: string) => {
    // Allow an empty box while typing; treat it as 0 on save and in totals.
    if (value === '' || parseFloat(value) >= 0) {
      setStatutoryDeductions((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handleAddSalaryComponent = () => {
    setSalaryComponents([
      ...salaryComponents,
      {
        id: `comp-${Date.now()}`,
        label: '',
        amount: '',
        percentage: '',
        operation: 'add',
        isPercentage: false,
      },
    ]);
  };

  const handleUpdateSalaryComponent = (
    compId: string,
    field: keyof SalaryComponent,
    value: any
  ) => {
    setSalaryComponents(
      salaryComponents.map((c) => (c.id === compId ? { ...c, [field]: value } : c))
    );
  };

  const handleDeleteSalaryComponent = (compId: string) => {
    setSalaryComponents(salaryComponents.filter((c) => c.id !== compId));
  };

  const handleUpdateEmployee = async () => {
    const gross = parseFloat(employeeForm.grossSalary || '0');
    if (gross <= 0) {
      setSnackbar({ open: true, message: 'Please enter a valid gross salary', severity: 'error' });
      return;
    }

    setLoading(true);

    try {
      // Prepare salary components for API
      const apiSalaryComponents = salaryComponents.map((c) => ({
        component_name: c.label,
        component_type: c.isPercentage ? 'percentage' : 'fixed',
        value: c.isPercentage ? Number(c.percentage || 0) : Number(c.amount || 0),
        operation: c.operation
      }));

      // Call the API to update employee payroll
      const response = await updateEmployeePayroll(parseInt(employeePayrollId!), {
        gross_salary: gross,
        salary_components: apiSalaryComponents,
        pf_contribution: Number(statutoryDeductions.pfContribution || 0),
        income_tax_tds: Number(statutoryDeductions.incomeTaxTds || 0),
        professional_tax: Number(statutoryDeductions.professionalTax || 0)
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update employee payroll');
      }

      setSnackbar({ open: true, message: 'Employee payroll updated successfully!', severity: 'success' });

      // Navigate back to payroll details after 1 second
      setTimeout(() => {
        navigate(`/payroll/${payrollId}`);
      }, 1000);
    } catch (error: any) {
      console.error('Error updating employee payroll:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update employee payroll',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <Layout>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg">
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate(`/payroll/${payrollId}`)}
              sx={{
                mb: 2,
                color: isDarkMode ? 'rgba(255, 255, 255, 0.87)' : 'inherit'
              }}
            >
              Back to Payroll Details
            </Button>
            <Typography variant="h4" gutterBottom>
              Edit Employee Payroll
            </Typography>
            {payrollInfo && (
              <Typography variant="subtitle1" color="textSecondary">
                {new Date(payrollInfo.start_date).toLocaleString('default', { month: 'long' })} {new Date(payrollInfo.start_date).getFullYear()}
              </Typography>
            )}
          </Box>

          {/* Form Card */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  fullWidth
                  label="Employee Name"
                  value={employeeForm.employeeName}
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                />

                <TextField
                  fullWidth
                  type="number"
                  label="Gross Salary *"
                  value={employeeForm.grossSalary}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, grossSalary: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">Rs.</InputAdornment>,
                  }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">Salary Components</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddSalaryComponent}
                    sx={{
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.87)' : 'inherit',
                      borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit'
                    }}
                  >
                    Add Component
                  </Button>
                </Box>

                {salaryComponents.length > 0 && (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell width={100}>Type</TableCell>
                          <TableCell width={120}>Value</TableCell>
                          <TableCell width={80}>Operation</TableCell>
                          <TableCell width={100} align="right">Calculated</TableCell>
                          <TableCell width={50} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {salaryComponents.map((comp) => (
                          <TableRow key={comp.id}>
                            <TableCell>
                              <TextField
                                size="small"
                                placeholder="e.g., HRA"
                                value={comp.label}
                                onChange={(e) =>
                                  handleUpdateSalaryComponent(comp.id, 'label', e.target.value)
                                }
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={comp.isPercentage ? 'percentage' : 'fixed'}
                                  onChange={(e) =>
                                    handleUpdateSalaryComponent(
                                      comp.id,
                                      'isPercentage',
                                      e.target.value === 'percentage'
                                    )
                                  }
                                >
                                  <MenuItem value="fixed">Rs. Fixed</MenuItem>
                                  <MenuItem value="percentage">% Percent</MenuItem>
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                value={comp.isPercentage ? comp.percentage : comp.amount}
                                onChange={(e) => {
                                  const val = e.target.value === '' ? '' : parseFloat(e.target.value);
                                  if (e.target.value === '' || val >= 0) {
                                    handleUpdateSalaryComponent(
                                      comp.id,
                                      comp.isPercentage ? 'percentage' : 'amount',
                                      val
                                    );
                                  }
                                }}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={comp.operation}
                                  onChange={(e) =>
                                    handleUpdateSalaryComponent(comp.id, 'operation', e.target.value as 'add' | 'subtract')
                                  }
                                >
                                  <MenuItem value="add"><AddIcon fontSize="small" sx={{ color: 'success.main' }} /></MenuItem>
                                  <MenuItem value="subtract"><Remove fontSize="small" sx={{ color: 'error.main' }} /></MenuItem>
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="caption"
                                color={comp.operation === 'add' ? 'success.main' : 'error.main'}
                              >
                                {comp.operation === 'add' ? '+' : '-'}Rs.
                                {calculateComponentValue(
                                  comp,
                                  parseFloat(employeeForm.grossSalary || '0')
                                ).toFixed(0)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteSalaryComponent(comp.id)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Statutory Deductions */}
                <Box>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>Statutory Deductions</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Deducted from net salary in addition to the salary components above. Leave as 0 if not applicable.
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                      gap: 2,
                    }}
                  >
                    {STATUTORY_FIELDS.map(({ key, label }) => (
                      <TextField
                        key={key}
                        fullWidth
                        type="number"
                        label={label}
                        value={statutoryDeductions[key]}
                        onChange={(e) => handleUpdateStatutory(key, e.target.value)}
                        inputProps={{ min: 0 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">Rs.</InputAdornment>,
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Salary Summary */}
                <Card variant="outlined" sx={{ bgcolor: isDarkMode ? 'grey.900' : 'grey.50', p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight="medium">Gross Salary:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      Rs.{(parseFloat(employeeForm.grossSalary || '0') || 0).toLocaleString()}
                    </Typography>
                  </Box>

                  {salaryComponents.length > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      {salaryComponents.map((comp) => {
                        const value = calculateComponentValue(comp, parseFloat(employeeForm.grossSalary || '0'));
                        return (
                          <Box key={comp.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography
                              variant="body2"
                              color={comp.operation === 'add' ? 'success.main' : 'error.main'}
                            >
                              {comp.label || 'Unnamed Component'}:
                            </Typography>
                            <Typography
                              variant="body2"
                              color={comp.operation === 'add' ? 'success.main' : 'error.main'}
                              fontWeight="medium"
                            >
                              {comp.operation === 'add' ? '+' : '-'}Rs.{value.toLocaleString()}
                            </Typography>
                          </Box>
                        );
                      })}
                    </>
                  )}

                  {calculateTotalStatutory() > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      {STATUTORY_FIELDS.map(({ key, label }) => {
                        const value = parseFloat(statutoryDeductions[key] || '0') || 0;
                        if (value === 0) return null;
                        return (
                          <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" color="error.main">{label}:</Typography>
                            <Typography variant="body2" color="error.main" fontWeight="medium">
                              -Rs.{value.toLocaleString()}
                            </Typography>
                          </Box>
                        );
                      })}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" fontWeight="medium">Total Statutory Deductions:</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          -Rs.{calculateTotalStatutory().toLocaleString()}
                        </Typography>
                      </Box>
                    </>
                  )}

                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Net Salary:</Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: isDarkMode ? '#90caf9' : 'primary.main'
                      }}
                    >
                      Rs.{calculateNetSalary().toLocaleString()}
                    </Typography>
                  </Box>
                </Card>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    onClick={() => navigate(`/payroll/${payrollId}`)}
                    disabled={loading}
                    sx={{
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.87)' : 'inherit'
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleUpdateEmployee}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                  >
                    {loading ? 'Updating...' : 'Update Employee'}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              variant="filled"
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Container>
    </Layout>
  );
};

export default EditEmployeePayroll;
